import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { PORTAL_KEYS } from './MarketContext';
import { clearGoalsSplashSeen } from '../utils/goalsSplash';
import { effectiveFootballOpsPermissions } from '../utils/footballOpsPermissions';

export const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * The auth value every portal reads through useAuth(), derived from a user's
 * roles and stored settings. Shared by AuthProvider and the admin portal
 * preview (components/PortalPreview.js), so a preview sees exactly what that
 * user would.
 */
export function buildAuthValue({ currentUser, userRoles, userPortalsRaw, footballOpsStored, loading = false, login, logout }) {
  const isAdmin = userRoles.includes('admin');
  const isStaffOrAdmin = userRoles.includes('staff') || userRoles.includes('admin');

  // Which staff portals this user can access. Admins get all. Otherwise use the
  // explicit `portals` array on the user doc; if it's missing (not yet
  // backfilled), fall back to the legacy per-portal roles so nobody loses access
  // mid-migration.
  const legacyPortals = [];
  if (userRoles.includes('staff')) legacyPortals.push('uk_to_spain');
  if (userRoles.includes('staff_es')) legacyPortals.push('spain_to_uk');
  if (userRoles.includes('staff_pad')) legacyPortals.push('padelante');
  const userPortals = isAdmin
    ? PORTAL_KEYS
    : (userPortalsRaw && userPortalsRaw.length ? userPortalsRaw : legacyPortals);
  const hasPortal = (key) => userPortals.includes(key);

  // Spanish-side / Padelante staff kept as derived flags for existing callers.
  const isSpainStaff = hasPortal('spain_to_uk') || isAdmin;
  const isPadStaff = hasPortal('padelante') || isAdmin;
  // What the Football Operations portal may show (everything, unless the
  // account's only role is football_ops and an admin has switched things off).
  const footballOpsPermissions = effectiveFootballOpsPermissions(userRoles, footballOpsStored);
  // Primary role kept for any code that still reads userRole directly
  const userRole = userRoles[0] ?? null;

  return { currentUser, userRole, userRoles, userPortals, hasPortal, isAdmin, isStaffOrAdmin, isSpainStaff, isPadStaff, footballOpsPermissions, loading, login, logout };
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userPortalsRaw, setUserPortalsRaw] = useState(null); // the stored `portals` array, or null
  const [footballOpsStored, setFootballOpsStored] = useState(null); // users/{uid}.footballOpsPermissions
  const [loading, setLoading] = useState(true);

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    // So the next sign-in gets the season-goals board again.
    clearGoalsSplashSeen();
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Support legacy role: string and new roles: string[] format
          const roles = Array.isArray(data.roles) ? data.roles
            : data.role ? [data.role]
            : [];
          setUserRoles(roles);
          setUserPortalsRaw(Array.isArray(data.portals) ? data.portals : null);
          setFootballOpsStored(data.footballOpsPermissions || null);
        }
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setUserRoles([]);
        setUserPortalsRaw(null);
        setFootballOpsStored(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = buildAuthValue({ currentUser, userRoles, userPortalsRaw, footballOpsStored, loading, login, logout });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
